import React, { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Video, Calendar, Tag, ArrowRight, Check, Ban, Play, ExternalLink, Plus } from 'lucide-react';
import logoUrl from '@assets/logo_square_1753226114988.png';
import { useAuth } from '@/contexts/use-auth';
import { getUserVideos, getUserCategories, updateVideo, stringToTimestamp, getAuthors, AuthorData } from '@/lib/firebase';
import AddCategoryModal from '@/components/add-category-modal';
import CalendarModal from '@/components/calendar-modal';
import { logger } from '@/lib/logger';
import { captureHandledException } from '@/lib/sentry';

interface Category {
  id?: string;
  name: string;
  title: string;
  contentLibrary: string;
  module: string;
}

interface ImportedVideo {
  id: string;
  externalId: string;
  title: string;
  description: string;
  url: string;
  author: string;
  channel: string;
  duration: string;
}

export default function ImportPage() {
  const [location, setLocation] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [step, setStep] = useState<'loading' | 'importing' | 'assigning'>('loading');
  const [categories, setCategories] = useState<Category[]>([]);
  const [importedVideos, setImportedVideos] = useState<ImportedVideo[]>([]);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [contributors, setContributors] = useState<AuthorData[]>([]);
  const [selectedContributorIds, setSelectedContributorIds] = useState<string[]>([]);
  const [noneContributor, setNoneContributor] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [moduleFilter, setModuleFilter] = useState<string>('All');
  const [publishDate, setPublishDate] = useState(new Date().toISOString().split('T')[0]);
  const [contentType, setContentType] = useState('information');
  const [isProcessing, setIsProcessing] = useState(false);
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [selectedVideoForDatePicker, setSelectedVideoForDatePicker] = useState<number | null>(null);

  useEffect(() => {
    if (user) {
      initializeImport();
    }
  }, [user]);

  const loadCategories = async () => {
    const categoriesData = await getUserCategories(user!.uid);
    setCategories(categoriesData.map(cat => ({ ...cat, name: cat.title })));
  };

  const loadContributors = async () => {
    const contributorsData = await getAuthors();
    setContributors(contributorsData);
  };

  const initializeImport = async () => {
    try {
      await Promise.all([loadCategories(), loadContributors()]);
      await loadDraftVideos();
    } catch (error: any) {
      logger.error('❌ Import initialization failed:', error);
      toast({
        title: "Failed to initialize import",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  // Videos arrive in Firestore with "Draft" status; this page assigns their
  // categories, contributors and publish dates.
  const loadDraftVideos = async () => {
    setStep('importing');

    try {
      const existingVideos = await getUserVideos(user!.uid);
      const videosToProcess = existingVideos.filter((video: any) => video.status === 'Draft');

      if (videosToProcess.length > 0) {
        const importedVideos = videosToProcess.map((video: any) => ({
          id: video.id,
          externalId: video.externalId,
          title: video.title,
          description: video.description || '',
          url: video.url,
          author: video.author,
          channel: video.channel,
          duration: video.duration,
        }));
        
        setImportedVideos(importedVideos);
        setStep('assigning');
        
        toast({
          title: "Ready to process videos",
          description: `Found ${videosToProcess.length} videos with "Draft" status ready for category assignment.`,
        });
        return;
      }

      toast({
        title: "No videos to process",
        description: "There are no videos with 'Draft' status.",
      });
      setLocation('/');
      
    } catch (error: any) {
      captureHandledException(error, "video-import.fetch");
      toast({
        title: "Failed to fetch videos",
        description: "Unable to load videos for processing. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleContributorToggle = (contributorId: string) => {
    setNoneContributor(false);
    setSelectedContributorIds(prev =>
      prev.includes(contributorId)
        ? prev.filter(id => id !== contributorId)
        : [...prev, contributorId]
    );
  };

  const handleNoneContributorToggle = () => {
    setNoneContributor(prev => {
      if (!prev) setSelectedContributorIds([]);
      return !prev;
    });
  };

  const handleCategoryToggle = (categoryId: string) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const getModuleColors = (module: string) => {
    switch (module?.toLowerCase()) {
      case 'body':
        return {
          border: 'border-[#d059c4]',
          bg: 'bg-[#d059c4]/10',
          selectedBorder: 'border-[#d059c4]',
          selectedBg: 'bg-[#d059c4]/20'
        };
      case 'mind':
        return {
          border: 'border-[#66cee7]',
          bg: 'bg-[#66cee7]/10',
          selectedBorder: 'border-[#66cee7]',
          selectedBg: 'bg-[#66cee7]/20'
        };
      case 'general':
        return {
          border: 'border-[#595ebe]',
          bg: 'bg-[#595ebe]/10',
          selectedBorder: 'border-[#595ebe]',
          selectedBg: 'bg-[#595ebe]/20'
        };
      default:
        return {
          border: 'border-slate-200',
          bg: 'bg-slate-50',
          selectedBorder: 'border-blue-500',
          selectedBg: 'bg-blue-50'
        };
    }
  };

  const getModuleChipColors = (module: string, isSelected: boolean) => {
    switch (module?.toLowerCase()) {
      case 'body':
        return isSelected 
          ? 'bg-[#d059c4] text-white border-[#d059c4]'
          : 'bg-[#d059c4]/10 text-[#d059c4] border-[#d059c4] hover:bg-[#d059c4]/20';
      case 'mind':
        return isSelected
          ? 'bg-[#66cee7] text-white border-[#66cee7]'
          : 'bg-[#66cee7]/10 text-[#66cee7] border-[#66cee7] hover:bg-[#66cee7]/20';
      case 'general':
        return isSelected
          ? 'bg-[#595ebe] text-white border-[#595ebe]'
          : 'bg-[#595ebe]/10 text-[#595ebe] border-[#595ebe] hover:bg-[#595ebe]/20';
      case 'all':
        return isSelected
          ? 'bg-slate-600 text-white border-slate-600'
          : 'bg-slate-100 text-slate-600 border-slate-300 hover:bg-slate-200';
      default:
        return 'bg-slate-100 text-slate-600 border-slate-300 hover:bg-slate-200';
    }
  };

  const getUniqueModules = () => {
    const modules = categories.map(cat => cat.module).filter(Boolean);
    return ['All', ...Array.from(new Set(modules))];
  };

  const filteredCategories = moduleFilter === 'All' 
    ? categories 
    : categories.filter(cat => cat.module === moduleFilter);

  const handleSaveVideo = async () => {
    if (selectedCategories.length === 0) {
      toast({
        title: "Categories required",
        description: "Please select at least one category before saving.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    
    try {
      const currentVideo = importedVideos[currentVideoIndex];
      
      // Get selected contributor objects
      const selectedContributorObjects = contributors.filter(c => selectedContributorIds.includes(c.id || ''));
      const contributorIds = selectedContributorObjects.map(c => c.id!);
      const contributorsPayload = selectedContributorObjects.map(c => ({
        id: c.id!,
        name: c.name,
        profilePicUrl: c.profilePicUrl || '',
        qualifications: c.qualifications || '',
        story: c.story || '',
      }));

      // Get selected category objects to extract additional fields
      const selectedCategoryObjects = categories.filter(cat => selectedCategories.includes(cat.id || ''));
      
      // Extract tag values for contentCategories
      const contentCategories = selectedCategoryObjects.map(cat => (cat as any).tag || cat.name);
      
      // Extract unique contentLibrary values (filter out null/undefined)
      const contentLibraries = Array.from(new Set(selectedCategoryObjects.map(cat => cat.contentLibrary).filter(Boolean)));
      
      // Extract unique module values (filter out null/undefined)
      const modules = Array.from(new Set(selectedCategoryObjects.map(cat => cat.module).filter(Boolean)));
      
      logger.log('Selected categories for import:', selectedCategoryObjects.map(cat => ({ name: cat.name, contentLibrary: cat.contentLibrary })));
      logger.log('Computed contentLibraries (should be distinct):', contentLibraries);
      
      await updateVideo(currentVideo.id, {
        contributorIds,
        contributors: contributorsPayload,
        contentCategories: contentCategories,
        contentLibraries: contentLibraries,
        modules: modules,
        publishDate: stringToTimestamp(publishDate),
        contentType: contentType,
        status: 'Published',
        episodeOnly: false
      });

      toast({
        title: "Video published",
        description: `"${currentVideo.title}" has been published.`,
      });

      // Move to next video or finish
      if (currentVideoIndex < importedVideos.length - 1) {
        setCurrentVideoIndex(currentVideoIndex + 1);
        setSelectedContributorIds([]);
        setNoneContributor(false);
        setSelectedCategories([]);
        setPublishDate(new Date().toISOString().split('T')[0]);
        setContentType('information');
      } else {
        // All videos processed, go back to dashboard
        toast({
          title: "Import complete",
          description: `All ${importedVideos.length} videos have been processed.`,
        });
        setLocation('/');
      }
      
    } catch (error: any) {
      captureHandledException(error, "video-import.save-video");
      toast({
        title: "Failed to save video",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveEpisode = async () => {
    if (selectedCategories.length === 0) {
      toast({
        title: "Categories required",
        description: "Please select at least one category before saving.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    
    try {
      const currentVideo = importedVideos[currentVideoIndex];

      const selectedContributorObjects = contributors.filter(c => selectedContributorIds.includes(c.id || ''));
      const contributorIds = selectedContributorObjects.map(c => c.id!);
      const contributorsPayload = selectedContributorObjects.map(c => ({
        id: c.id!,
        name: c.name,
        profilePicUrl: c.profilePicUrl || '',
        qualifications: c.qualifications || '',
        story: c.story || '',
      }));
      
      const selectedCategoryObjects = categories.filter(cat => selectedCategories.includes(cat.id || ''));
      
      const contentCategories = selectedCategoryObjects.map(cat => (cat as any).tag || cat.name);
      
      const contentLibraries = Array.from(new Set(selectedCategoryObjects.map(cat => cat.contentLibrary).filter(Boolean)));
      
      const modules = Array.from(new Set(selectedCategoryObjects.map(cat => cat.module).filter(Boolean)));
      
      await updateVideo(currentVideo.id, {
        contributorIds,
        contributors: contributorsPayload,
        contentCategories: contentCategories,
        contentLibraries: contentLibraries,
        modules: modules,
        publishDate: stringToTimestamp(publishDate),
        contentType: contentType,
        status: 'Published',
        episodeOnly: true
      });

      toast({
        title: "Episode published",
        description: `"${currentVideo.title}" has been published as an episode.`,
      });

      if (currentVideoIndex < importedVideos.length - 1) {
        setCurrentVideoIndex(currentVideoIndex + 1);
        setSelectedContributorIds([]);
        setNoneContributor(false);
        setSelectedCategories([]);
        setPublishDate(new Date().toISOString().split('T')[0]);
        setContentType('information');
      } else {
        toast({
          title: "Import complete",
          description: `All ${importedVideos.length} videos have been processed.`,
        });
        setLocation('/');
      }
      
    } catch (error: any) {
      captureHandledException(error, "video-import.save-episode");
      toast({
        title: "Failed to save episode",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSkipVideo = () => {
    if (currentVideoIndex < importedVideos.length - 1) {
      setCurrentVideoIndex(currentVideoIndex + 1);
      setSelectedContributorIds([]);
      setNoneContributor(false);
      setSelectedCategories([]);
      setPublishDate(new Date().toISOString().split('T')[0]);
      setContentType('information');
    } else {
      setLocation('/');
    }
  };

  const handleBlockVideo = async () => {
    setIsProcessing(true);
    
    try {
      const currentVideo = importedVideos[currentVideoIndex];
      
      await updateVideo(currentVideo.id, {
        status: 'Blocked'
      });

      toast({
        title: "Video blocked",
        description: `"${currentVideo.title}" has been blocked and will not be displayed.`,
      });

      // Move to next video or finish
      if (currentVideoIndex < importedVideos.length - 1) {
        setCurrentVideoIndex(currentVideoIndex + 1);
        setSelectedContributorIds([]);
        setNoneContributor(false);
        setSelectedCategories([]);
        setPublishDate(new Date().toISOString().split('T')[0]);
        setContentType('information');
      } else {
        // All videos processed, go back to dashboard
        toast({
          title: "Import complete",
          description: `All ${importedVideos.length} videos have been processed.`,
        });
        setLocation('/');
      }
      
    } catch (error: any) {
      captureHandledException(error, "video-import.block-video");
      toast({
        title: "Failed to block video",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-600">Initializing video import...</p>
        </div>
      </div>
    );
  }

  if (step === 'importing') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-600">Loading draft videos...</p>
          <p className="text-sm text-slate-500 mt-2">This may take a few moments</p>
        </div>
      </div>
    );
  }

  if (step === 'assigning' && importedVideos.length > 0) {
    const currentVideo = importedVideos[currentVideoIndex];
    const progress = ((currentVideoIndex + 1) / importedVideos.length) * 100;

    return (
      <div className="min-h-screen bg-slate-50">
        {/* Navigation */}
        <nav className="bg-white border-b border-slate-200 px-6 py-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setLocation('/')}
                className="flex items-center space-x-2"
              >
                <ArrowLeft size={16} />
                <span>Back to Dashboard</span>
              </Button>
              <div className="h-6 w-px bg-slate-200"></div>
              <div className="flex items-center space-x-2">
                <div className="h-6 w-6 bg-white rounded flex items-center justify-center">
                  <img src={logoUrl} alt="Wellbound Logo" className="h-5 w-5" />
                </div>
                <h1 className="text-lg font-semibold text-slate-900">Video Import</h1>
              </div>
            </div>
            
            <div className="text-sm text-slate-600">
              Video {currentVideoIndex + 1} of {importedVideos.length}
            </div>
          </div>
        </nav>

        {/* Progress Bar */}
        <div className="bg-white border-b border-slate-200 px-6 py-2">
          <div className="max-w-4xl mx-auto">
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 py-4">
          {/* Video Info Header */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-start space-x-3">
                  <div>
                    <h2 className="text-lg font-bold text-slate-900 mb-1">{currentVideo.title}</h2>
                    <div className="flex items-center space-x-4 text-sm text-slate-600">
                      <span>{currentVideo.channel} • {currentVideo.author}</span>
                      <span>Duration: {currentVideo.duration}</span>
                    </div>
                  </div>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="flex items-center space-x-2">
                        <Play size={16} />
                        <span>Preview</span>
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl w-full">
                      <DialogHeader>
                        <DialogTitle>{currentVideo.title}</DialogTitle>
                      </DialogHeader>
                      <div className="aspect-video w-full">
                        <iframe
                          src={currentVideo.url}
                          className="w-full h-full rounded-lg"
                          frameBorder="0"
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                          title={currentVideo.title}
                        />
                      </div>
                      <div className="flex items-center justify-between text-sm text-slate-600">
                        <span>{currentVideo.channel} • {currentVideo.author}</span>
                        <Button variant="outline" size="sm" asChild>
                          <a href={currentVideo.url} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-1">
                            <ExternalLink size={14} />
                            <span>Open in Vimeo</span>
                          </a>
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <Label htmlFor="contentType" className="text-sm font-medium text-slate-700 w-24">
                      Content Type:
                    </Label>
                    <Select value={contentType} onValueChange={setContentType}>
                      <SelectTrigger className="w-36 ml-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="information">Information</SelectItem>
                        <SelectItem value="instructional">Instructional</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex items-center">
                    <Label htmlFor="publishDate" className="text-sm font-medium text-slate-700 w-24">
                      Publish Date:
                    </Label>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSelectedVideoForDatePicker(currentVideoIndex);
                        setShowCalendarModal(true);
                      }}
                      className="w-36 ml-2 justify-start text-left font-normal"
                    >
                      <Calendar size={16} className="mr-2" />
                      {publishDate ? new Date(publishDate).toLocaleDateString() : 'Select date'}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contributors */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-lg">
                <span>Select Contributors</span>
                <span className="text-sm font-normal text-slate-500">(optional)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-2">
                {/* None tile */}
                <button
                  onClick={handleNoneContributorToggle}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-colors text-sm font-medium ${
                    noneContributor
                      ? 'bg-slate-600 text-white border-slate-600'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400 hover:bg-slate-50'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${noneContributor ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-600'}`}>
                    —
                  </div>
                  <span>None</span>
                  {noneContributor && <Check size={14} />}
                </button>
                {contributors.map((contributor) => {
                  const isSelected = selectedContributorIds.includes(contributor.id || '');
                  return (
                    <button
                      key={contributor.id}
                      onClick={() => handleContributorToggle(contributor.id || '')}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-colors text-sm font-medium ${
                        isSelected
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white text-slate-700 border-slate-200 hover:border-primary hover:bg-primary/5'
                      }`}
                    >
                      {contributor.profilePicUrl ? (
                        <img
                          src={contributor.profilePicUrl}
                          alt={contributor.name}
                          className="w-6 h-6 rounded-full object-cover"
                        />
                      ) : (
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${isSelected ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-600'}`}>
                          {contributor.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span>{contributor.name}</span>
                      {isSelected && <Check size={14} />}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Categories - Main Focus */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between text-lg">
                <div className="flex items-center space-x-2">
                  <Tag size={24} />
                  <span>Select Categories</span>
                  <span className="text-sm font-normal text-slate-500">(at least one required)</span>
                </div>
                {/* Module Filter Chips */}
                <div className="flex gap-2">
                  {getUniqueModules().map((module) => (
                    <button
                      key={module}
                      onClick={() => setModuleFilter(module)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-colors ${
                        getModuleChipColors(module, moduleFilter === module)
                      }`}
                    >
                      {module}
                    </button>
                  ))}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">

              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {filteredCategories.map((category) => {
                  const colors = getModuleColors(category.module);
                  const isSelected = selectedCategories.includes(category.id || '');
                  
                  return (
                    <div 
                      key={category.id} 
                      className={`border rounded p-2 cursor-pointer transition-colors ${
                        isSelected 
                          ? `${colors.selectedBorder} ${colors.selectedBg}` 
                          : `${colors.border} ${colors.bg} hover:${colors.selectedBg}`
                      }`}
                      onClick={() => handleCategoryToggle(category.id || '')}
                    >
                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id={category.id || `category-${Math.random()}`}
                        checked={selectedCategories.includes(category.id || '')}
                        onCheckedChange={() => handleCategoryToggle(category.id || '')}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900 text-sm leading-tight">
                          {category.name || category.title}
                        </div>
                        <div className="text-xs text-slate-600 mt-0.5">
                          {category.contentLibrary} - {category.module}
                        </div>
                      </div>
                      </div>
                    </div>
                  );
                })}
                
                {/* Create New Category Tile */}
                <div 
                  className="border border-dashed border-slate-300 rounded p-2 cursor-pointer transition-colors hover:border-primary hover:bg-primary/5"
                  onClick={() => setAddCategoryOpen(true)}
                >
                  <div className="flex items-center justify-center space-x-2 h-full min-h-[60px]">
                    <Plus className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-600 font-medium">Create New Category</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-4 mt-4 border-t border-slate-200">
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    onClick={handleSkipVideo}
                    disabled={isProcessing}
                  >
                    Skip Video
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={handleBlockVideo}
                    disabled={isProcessing}
                    className="flex items-center space-x-2 text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                  >
                    <Ban size={16} />
                    <span>Do Not Publish</span>
                  </Button>
                </div>
                
                <Button
                  onClick={handleSaveVideo}
                  disabled={isProcessing || (!noneContributor && selectedContributorIds.length === 0) || selectedCategories.length === 0}
                  className="flex items-center space-x-2"
                  size="lg"
                >
                  {isProcessing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <Check size={16} />
                      <span>Publish Video</span>
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleSaveEpisode}
                  disabled={isProcessing || (!noneContributor && selectedContributorIds.length === 0) || selectedCategories.length === 0}
                  className="flex items-center space-x-2"
                  size="lg"
                  variant="secondary"
                >
                  {isProcessing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <Check size={16} />
                      <span>Publish Episode</span>
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Add Category Modal */}
        <AddCategoryModal
          open={addCategoryOpen}
          onClose={() => setAddCategoryOpen(false)}
          onCategoryAdded={() => {
            setAddCategoryOpen(false);
            loadCategories();
          }}
        />

        {/* Calendar Modal */}
        <CalendarModal
          isOpen={showCalendarModal}
          onClose={() => {
            setShowCalendarModal(false);
            setSelectedVideoForDatePicker(null);
          }}
          onDateSelect={(date: string) => {
            setPublishDate(date);
            setShowCalendarModal(false);
            setSelectedVideoForDatePicker(null);
          }}
          selectedDate={publishDate}
        />
      </div>
    );
  }

  return null;
}